import { useEffect, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Mail,
  MessageSquare,
  Plus,
  Loader2,
  Edit,
  Trash2,
  FileSignature,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { RichTextEditor } from "@/components/RichTextEditor"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  templatesApi,
  type TemplateListItem,
  type TemplateType,
  type TemplateInput,
} from "@/lib/api"
import { timeAgo } from "@shared/utils/date"
import { cn } from "@/lib/utils"
import { useAuth } from "@/hooks/useAuth"

export default function TemplatesPage() {
  const [tab, setTab] = useState<TemplateType>("email")
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<TemplateListItem | null>(null)
  const [sigOpen, setSigOpen] = useState(false)

  const list = useQuery({
    queryKey: ["templates", tab],
    queryFn: () => templatesApi.list(tab),
  })

  const actions = (
    <div className="flex gap-2">
      <Button variant="outline" onClick={() => setSigOpen(true)}>
        <FileSignature className="h-4 w-4" />
        My Signature
      </Button>
      <Button
        onClick={() => {
          setEditing(null)
          setEditorOpen(true)
        }}
      >
        <Plus className="h-4 w-4" />
        New Template
      </Button>
    </div>
  )

  return (
    <AppShell title="Templates" subtitle="Email and SMS templates for common communications" actions={actions}>
      <div className="space-y-4">
        <div className="flex gap-2">
          {(["email", "sms"] as TemplateType[]).map((t) => {
            const Icon = t === "email" ? Mail : MessageSquare
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  "flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium",
                  tab === t
                    ? "border-rc-blue-600 bg-rc-blue-600 text-white"
                    : "border-border bg-background hover:bg-accent"
                )}
              >
                <Icon className="h-4 w-4" />
                {t === "email" ? "Email Templates" : "SMS Templates"}
              </button>
            )
          })}
        </div>

        {list.isLoading ? (
          <Card className="grid place-items-center p-16">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </Card>
        ) : !list.data?.items.length ? (
          <Card className="p-12 text-center">
            <p className="text-sm font-medium text-foreground">
              No {tab} templates yet
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              Create your first template to speed up common {tab === "email" ? "emails" : "SMS messages"}.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {list.data.items.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                onEdit={() => {
                  setEditing(t)
                  setEditorOpen(true)
                }}
              />
            ))}
          </div>
        )}
      </div>

      <TemplateEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editing}
        defaultType={tab}
      />
      <SignatureDialog open={sigOpen} onOpenChange={setSigOpen} />
    </AppShell>
  )
}

function TemplateCard({
  template,
  onEdit,
}: {
  template: TemplateListItem
  onEdit: () => void
}) {
  const qc = useQueryClient()
  const { user } = useAuth()

  const remove = useMutation({
    mutationFn: () => templatesApi.remove(template.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  })

  const canEdit =
    user &&
    (["admin", "manager"].includes(user.role) || template.ownerId === user.id)

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-foreground">{template.name}</h3>
            {template.isShared && (
              <Badge variant="info" className="text-[10px]">
                Shared
              </Badge>
            )}
            {template.category && (
              <Badge variant="muted" className="text-[10px]">
                {template.category}
              </Badge>
            )}
            {!template.isActive && (
              <Badge variant="muted" className="text-[10px]">
                Inactive
              </Badge>
            )}
          </div>
          {template.subject && (
            <p className="text-muted-foreground mt-1 truncate text-xs">
              Subject: {template.subject}
            </p>
          )}
          <p className="text-muted-foreground mt-2 line-clamp-3 text-sm whitespace-pre-wrap">
            {template.body}
          </p>
          <div className="text-muted-foreground mt-3 flex items-center gap-2 text-xs">
            <span>{template.ownerName ?? "—"}</span>
            <span>·</span>
            <span>Updated {timeAgo(template.updatedAt)}</span>
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-col gap-1">
            <Button variant="ghost" size="icon" onClick={onEdit} title="Edit">
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (confirm(`Delete "${template.name}"?`)) remove.mutate()
              }}
              className="hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

function TemplateEditor({
  open,
  onOpenChange,
  template,
  defaultType,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  template: TemplateListItem | null
  defaultType: TemplateType
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState<TemplateInput>({
    name: "",
    type: defaultType,
    category: null,
    subject: null,
    body: "",
    includeSignature: false,
    isShared: false,
    isActive: true,
  })
  const [error, setError] = useState<string | null>(null)

  const mergeFields = useQuery({
    queryKey: ["templates-merge-fields"],
    queryFn: () => templatesApi.mergeFields(),
    enabled: open,
  })

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name,
        type: template.type,
        category: template.category,
        subject: template.subject,
        body: template.body,
        includeSignature: template.includeSignature,
        isShared: template.isShared,
        isActive: template.isActive,
      })
    } else {
      setForm({
        name: "",
        type: defaultType,
        category: null,
        subject: null,
        body: "",
        includeSignature: false,
        isShared: false,
        isActive: true,
      })
    }
    setError(null)
  }, [template, defaultType, open])

  const save = useMutation({
    mutationFn: () =>
      template
        ? templatesApi.update(template.id, form).then(() => ({ id: template.id }))
        : templatesApi.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["templates"] })
      onOpenChange(false)
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Save failed"),
  })

  const set = <K extends keyof TemplateInput>(k: K, v: TemplateInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const insertMergeField = (key: string) => {
    set("body", `${form.body}{{${key}}}`)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{template ? "Edit Template" : "New Template"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            if (!form.name.trim()) {
              setError("Name required")
              return
            }
            if (!form.body.trim()) {
              setError("Body required")
              return
            }
            save.mutate()
          }}
          className="space-y-3"
        >
          {error && (
            <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>
            <div>
              <Label>Type</Label>
              <select
                className="border-input flex h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={form.type}
                onChange={(e) => set("type", e.target.value as TemplateType)}
                disabled={!!template}
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Category</Label>
            <Input
              placeholder="e.g. follow-up, document-request"
              value={form.category ?? ""}
              onChange={(e) => set("category", e.target.value || null)}
            />
          </div>

          {form.type === "email" && (
            <div>
              <Label>Subject</Label>
              <Input
                value={form.subject ?? ""}
                onChange={(e) => set("subject", e.target.value || null)}
              />
            </div>
          )}

          <div>
            <Label>Body *</Label>
            <Textarea
              required
              rows={form.type === "email" ? 8 : 4}
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
              placeholder={
                form.type === "sms"
                  ? "Hi {{first_name}}, this is {{broker_name}}…"
                  : "Hi {{first_name}},\n\n…\n\nRegards,\n{{broker_name}}"
              }
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Available merge fields (click to insert):
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {mergeFields.data?.fields.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => insertMergeField(f.key)}
                  className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono hover:bg-accent"
                  title={f.label}
                >
                  {`{{${f.key}}}`}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 border-t border-border pt-3">
            {form.type === "email" && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.includeSignature}
                  onChange={(e) => set("includeSignature", e.target.checked)}
                />
                Append my signature
              </label>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isShared}
                onChange={(e) => set("isShared", e.target.checked)}
              />
              Share with team
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set("isActive", e.target.checked)}
              />
              Active
            </label>
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {template ? "Save Changes" : "Create Template"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function SignatureDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const qc = useQueryClient()
  const [html, setHtml] = useState("")

  const sig = useQuery({
    queryKey: ["email-signature"],
    queryFn: () => templatesApi.getSignature(),
    enabled: open,
  })

  useEffect(() => {
    if (sig.data?.signature) setHtml(sig.data.signature.html)
    else setHtml("")
  }, [sig.data, open])

  const save = useMutation({
    mutationFn: () => templatesApi.saveSignature(html),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-signature"] })
      onOpenChange(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>My Email Signature</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm">
            Rich text signature appended to every automation and template email
            when “Include my signature” is enabled. Add your name, phone, and
            logo — use the image button to embed a photo or logo.
          </p>
          <RichTextEditor
            value={html}
            onChange={setHtml}
            placeholder="Regards, Your Name..."
            minHeight={180}
          />
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Signature
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
